import type { AppRole, Capability } from './permissions.ts';

/**
 * API conventions
 * - JSON field names mirror D1 column names (snake_case) so a value can always be
 *   traced back to the row that produced it.
 * - Every monetary field ends in `_minor` and is an integer number of cents.
 * - Dates are `YYYY-MM-DD`; timestamps are ISO-8601 UTC strings.
 * - List endpoints accept `page`, `pageSize`, `search` and return `Paginated<T>`.
 */

export interface ApiErrorBody {
  error: {
    code:
      | 'validation_failed'
      | 'unauthenticated'
      | 'forbidden'
      | 'not_found'
      | 'conflict'
      | 'rate_limited'
      | 'unprocessable'
      | 'server_error';
    message: string;
    details?: { path: string; message: string }[];
    correlation_id?: string;
  };
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface SessionInfo {
  user: {
    id: number;
    email: string;
    username: string;
    full_name: string;
    role: AppRole;
    status: string;
    must_change_password: boolean;
    last_login_at: string | null;
  };
  capabilities: Capability[];
  csrf_token: string;
  session_expires_at: string;
  school: { name: string; currency: string };
}

export interface GuardianRow {
  id: number;
  full_name: string;
  relationship: string;
  phone_primary: string | null;
  phone_alternate: string | null;
  email: string | null;
  address: string | null;
  occupation: string | null;
  notes: string | null;
  is_primary: number;
  is_emergency: number;
  link_id: number;
}

export interface StudentRow {
  id: number;
  student_number: string;
  first_name: string;
  last_name: string;
  other_names: string | null;
  date_of_birth: string | null;
  gender: string | null;
  class_id: number | null;
  class_label: string | null;
  academic_year_id: number | null;
  academic_year_label: string | null;
  enrollment_date: string | null;
  status: string;
  contact_phone: string | null;
  contact_email: string | null;
  residential_address: string | null;
  postal_address: string | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CurrencyAmounts {
  currency: string;
  billed_minor: number;
  paid_minor: number;
  balance_minor: number;
  overdue_minor: number;
}

/**
 * Payment status is derived from the money on the account, never from the
 * invoice flag, and it is only ever a label for the figures beside it - the
 * balance itself stays the authority.
 */
export type AccountPaymentStatus =
  | 'NOT CHARGED YET'
  | 'UNPAID'
  | 'PARTIALLY PAID'
  | 'PAID IN FULL'
  | 'CREDIT BALANCE';

/** One academic year / term slice of a learner's account. */
export interface AccountPeriodAmounts {
  academic_year_id: number;
  year_label: string;
  term_id: number | null;
  term_label: string;
  currency: string;
  charges_minor: number;
  discounts_minor: number;
  payments_minor: number;
  credits_minor: number;
  /** Balance closed off at a year end and re-opened in the following year. */
  carried_out_minor: number;
  balance_minor: number;
  /** Whole-year bill (no term): the amount is due across the year, not one term. */
  year_wide: boolean;
  /** True for the open year's current term - the debt the office collects now. */
  current: boolean;
  /** This period is the brought-forward balance from a closed year. */
  carried_forward: boolean;
  /** Every bill in this period was carried into a later year. */
  superseded: boolean;
}

/** Everything the student fees profile needs, on one consistent calculation. */
export interface StudentFeesSummary {
  currency: string;
  charges_minor: number;
  discounts_minor: number;
  additional_charges_minor: number;
  payments_minor: number;
  credits_minor: number;
  /** Movements of balance into a later year - never counted as money received. */
  carried_out_minor: number;
  balance_minor: number;
  overdue_minor: number;
  payment_status: AccountPaymentStatus;
  /** Amounts the ledger cannot settle cleanly; shown as a review flag, never as 0. */
  needs_review: boolean;
  review_reason: string | null;
  current_term_balance_minor: number;
  carried_forward_balance_minor: number;
  total_balance_minor: number;
  unbilled_structures: number;
  periods: AccountPeriodAmounts[];
}

/**
 * One currency's position on a student account. The accounts list reports these
 * per currency (and per student) so a mixed USD/ZiG learner is never shown as a
 * single meaningless total.
 */
export interface AccountCurrencyAmounts {
  currency: string;
  billed_minor: number;
  received_minor: number;
  credited_minor: number;
  outstanding_minor: number;
  overdue_minor: number;
}

export interface StudentSummary {
  billed_minor: number;
  paid_minor: number;
  balance_minor: number;
  overdue_minor: number;
  last_payment_at: string | null;
  open_invoice_count: number;
  /** Currency the scalar amounts are expressed in (the account's primary currency). */
  currency: string;
  /** Per-currency breakdown; always present so mixed accounts never read as one pot. */
  by_currency: CurrencyAmounts[];
}

export interface LedgerEntry {
  id: string;
  occurred_at: string;
  kind: 'invoice' | 'payment' | 'adjustment' | 'reversal' | 'carry_in' | 'carry_out';
  reference: string;
  description: string;
  debit_minor: number;
  credit_minor: number;
  currency: string;
  running_balance_minor: number;
  invoice_id: number | null;
  payment_id: number | null;
  adjustment_id: number | null;
  term_label: string | null;
}

export interface StudentDetail {
  student: StudentRow;
  guardians: GuardianRow[];
  summary: StudentSummary;
  invoices: InvoiceRow[];
  payments: PaymentRow[];
  ledger: LedgerEntry[];
  documents: DocumentRow[];
  audit: AuditEventRow[];
}

export interface InvoiceRow {
  id: number;
  invoice_number: string;
  student_id: number;
  student_number: string | null;
  student_name: string | null;
  academic_year_id: number;
  term_id: number | null;
  term_label: string | null;
  class_id_snapshot: number | null;
  class_label: string | null;
  issue_date: string;
  due_date: string | null;
  currency: string;
  subtotal_minor: number;
  discount_minor: number;
  total_minor: number;
  /** The figure on the bill when it was issued, kept when a fee change re-prices it. */
  revised_minor: number | null;
  paid_minor: number;
  balance_minor: number;
  status: string;
  notes: string | null;
  created_at: string;
  issued_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
}

export interface InvoiceLineRow {
  id: number;
  invoice_id: number;
  category_id: number;
  category_name: string | null;
  description: string;
  unit_amount_minor: number;
  quantity: number;
  line_total_minor: number;
}

export interface InvoiceDetail {
  invoice: InvoiceRow;
  lines: InvoiceLineRow[];
  payments: PaymentRow[];
  school: { name: string; address: string | null; currency: string };
}

export interface PaymentRow {
  id: number;
  receipt_id: number | null;
  receipt_number: string | null;
  student_id: number;
  student_number: string | null;
  student_name: string | null;
  amount_minor: number;
  allocated_minor: number;
  currency: string;
  paid_at: string;
  payment_method: string;
  reference: string | null;
  status: string;
  notes: string | null;
  recorded_by: number | null;
  recorded_by_name: string | null;
  created_at: string;
  reversed_at: string | null;
  reversal_reason: string | null;
  original_payment_id: number | null;
  allocations: {
    invoice_id: number;
    invoice_number: string;
    amount_minor: number;
  }[];
}

export interface ReceiptRow {
  id: number;
  receipt_number: string;
  student_id: number;
  student_number: string | null;
  student_name: string | null;
  amount_minor: number;
  currency: string;
  issued_at: string;
  issued_by: number | null;
  issued_by_name: string | null;
  payment_id: number | null;
  payment_method: string | null;
  reference: string | null;
  reprint_count: number;
  voided_at: string | null;
  covers: { invoice_number: string; description: string; amount_minor: number }[];
  balance_after_minor: number | null;
}

export interface AdjustmentRow {
  id: number;
  student_id: number;
  student_name: string | null;
  invoice_id: number | null;
  kind: string;
  direction: string;
  amount_minor: number;
  currency: string;
  reason: string;
  status: string;
  approval_id: number | null;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
  reversed_at: string | null;
}

export interface FeeStructureRow {
  id: number;
  academic_year_id: number;
  academic_year_label: string | null;
  term_id: number | null;
  term_label: string | null;
  class_id: number;
  class_label: string | null;
  category_id: number;
  category_name: string | null;
  description: string;
  amount_minor: number;
  currency: string;
  due_date: string | null;
  mandatory: number;
  applicable_groups: string | null;
  /** Other classes (streams) this fee is linked to; billing applies it to all of them. */
  group_class_ids: number[];
  valid_from: string | null;
  valid_to: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * What one student owes on one fee structure. A payment settles the structures
 * on the invoice it was applied to, oldest line first, so an initial payment on
 * one fee shows up against that fee instead of clearing the whole account.
 */
export interface StructureLiabilityRow {
  fee_structure_id: number;
  description: string;
  category_id: number;
  category_name: string | null;
  currency: string;
  academic_year_id: number;
  year_label: string | null;
  term_id: number | null;
  term_label: string | null;
  due_date: string | null;
  billed_minor: number;
  settled_minor: number;
  balance_minor: number;
  overdue: number;
  /** 0 when the fee applies to the student's group but no invoice carries it yet. */
  billed: number;
}

export interface FeeCategoryRow {  id: number;
  code: string;
  name: string;
  description: string | null;
  active: number;
}

export interface AcademicYearRow {
  id: number;
  label: string;
  start_date: string;
  end_date: string;
  is_current: number;
  /** Set once the year's balances have been carried forward; the stream is then closed. */
  closed_at?: string | null;
}

export interface CurrencyCarry {
  currency: string;
  carried_minor: number;
}

/** What closing a year would do, calculated without writing anything. */
export interface YearRolloverPreview {
  academic_year_id: number;
  students_owing: number;
  invoices_carried: number;
  by_currency: CurrencyCarry[];
  already_carried: number;
  year_closed_at: string | null;
}

export interface YearRolloverResult extends YearRolloverPreview {
  close_id: number;
  complete: boolean;
  to_academic_year_id: number;
  opened_new_year: boolean;
}

export interface YearCloseRow {
  id: number;
  from_academic_year_id: number;
  to_academic_year_id: number;
  from_year_label: string;
  to_year_label: string;
  closed_at: string | null;
  students_moved: number;
  invoices_carried: number;
  totals_json: string;
  notes: string | null;
  closed_by_name: string | null;
}

export interface TermRow {
  id: number;
  academic_year_id: number;
  label: string;
  position: number;
  start_date: string;
  end_date: string;
  fee_due_date: string | null;
  is_current: number;
  academic_year_label?: string | null;
}

export interface ClassRow {
  id: number;
  label: string;
  grade_level: number | null;
  stream: string | null;
  active: number;
}

export interface UserRow {
  id: number;
  email: string;
  username: string;
  full_name: string;
  role: AppRole;
  status: string;
  must_change_password: number;
  last_login_at: string | null;
  created_at: string;
  active_sessions: number;
}

export interface ApprovalRow {
  id: number;
  request_type: string;
  status: string;
  student_id: number | null;
  student_name: string | null;
  entity_type: string | null;
  entity_id: number | null;
  amount_minor: number | null;
  currency: string | null;
  justification: string;
  payload_json: string;
  requested_by: number;
  requested_by_name: string | null;
  requested_at: string;
  decided_by: number | null;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_notes: string | null;
  can_decide: boolean;
}

export interface AuditEventRow {
  id: number;
  actor_user_id: number | null;
  actor_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  outcome: string;
  metadata_json: string | null;
  correlation_id: string | null;
  created_at: string;
}

export interface DocumentRow {
  id: number;
  student_id: number;
  original_name: string;
  media_type: string;
  size_bytes: number;
  status: string;
  uploaded_by: number | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

export interface BackupRow {
  id: number;
  status: string;
  started_at: string;
  finished_at: string | null;
  size_bytes: number | null;
  row_count: number | null;
  checksum: string | null;
  r2_key: string | null;
  verified: number;
  verify_note?: string | null;
  error: string | null;
  triggered_by: string;
  /** Set once the retention policy has removed the archive from R2. */
  pruned_at?: string | null;
}

export interface DashboardData {
  generated_at: string;
  period: { from: string; to: string; label: string };
  metrics: {
    active_students: number;
    /** Every scalar below is expressed in base_currency (converted when needed). */
    billed_minor: number;
    /**
     * Part of `billed_minor` that was carried into this year from an earlier
     * one. Shown separately so "fees billed this year" can be read as
     * `billed_minor - brought_forward_minor`.
     */
    brought_forward_minor: number;
    received_minor: number;
    outstanding_minor: number;
    overdue_minor: number;
    /**
     * What the fee structures in force add up to for every active learner
     * (structure amount x learners in that class). Compared against
     * `billed_minor` the gap is what has not been invoiced yet.
     */
    expected_minor: number;
    /** expected_minor - (billed_minor - brought_forward_minor), never negative. */
    unbilled_minor: number;
    pending_approvals: number;
    collection_rate_percent: number;
    base_currency: string;
    by_currency: {
      currency: string;
      billed_minor: number;
      brought_forward_minor: number;
      received_minor: number;
      outstanding_minor: number;
      overdue_minor: number;
      expected_minor: number;
    }[];
    /** Present only when figures above include converted amounts, e.g. "1 USD = 15.00 ZiG". */
    conversion: { rate: number; label: string } | null;
  };
  financial_access: boolean;
  trend: { bucket: string; received_minor: number; outstanding_minor: number }[];
  by_class: {
    class_label: string;
    billed_minor: number;
    received_minor: number;
    outstanding_minor: number;
    expected_minor: number;
  }[];
  recent_payments: PaymentRow[];
  recent_students: { id: number; student_number: string; full_name: string; class_label: string | null; created_at: string }[];
  backup: { status: string; finished_at: string | null; verified: number } | null;
}

export interface ReportColumn {
  key: string;
  label: string;
  type: 'text' | 'money' | 'date' | 'number';
}

export interface ReportData {
  report: string;
  title: string;
  columns: ReportColumn[];
  rows: Record<string, string | number | null>[];
  /** Sums for the dominant currency only - currencies are never added together. */
  totals: Record<string, number>;
  totals_by_currency: { currency: string; totals: Record<string, number> }[];
  filters: Record<string, unknown>;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface StatementData {
  student: StudentRow;
  guardians: GuardianRow[];
  from: string;
  to: string;
  opening_balance_minor: number;
  closing_balance_minor: number;
  currency: string;
  by_currency: {
    currency: string;
    opening_balance_minor: number;
    closing_balance_minor: number;
  }[];
  entries: LedgerEntry[];
  school: { name: string; address: string | null; currency: string; phone: string | null };
}

export interface HealthData {
  status: string;
  environment: string;
  version: string;
  database: string;
  storage: { backups: boolean; documents: boolean };
  counts: { students: number; payments: number };
  last_backup: BackupRow | null;
}

/** Route map - the single place where the client and worker agree on paths. */
export const ROUTES = {
  session: '/api/auth/session',
  login: '/api/auth/login',
  logout: '/api/auth/logout',
  forgotPassword: '/api/auth/forgot-password',
  validateReset: '/api/auth/reset-password/validate',
  resetPassword: '/api/auth/reset-password',
  changePassword: '/api/auth/change-password',
  dashboard: '/api/dashboard',
  health: '/health',
  students: '/api/students',
  student: (id: number | string) => `/api/students/${id}`,
  studentArchive: (id: number | string) => `/api/students/${id}/archive`,
  studentReactivate: (id: number | string) => `/api/students/${id}/reactivate`,
  studentGuardians: (id: number | string) => `/api/students/${id}/guardians`,
  studentLedger: (id: number | string) => `/api/students/${id}/ledger`,
  documents: '/api/documents',
  /** Guardians are records inside a student profile, so documents are filtered by student. */
  studentDocuments: (id: number | string) => `/api/documents?student_id=${id}`,
  documentDownload: (id: number | string) => `/api/documents/${id}/download`,
  /** Server-side conversion of the stored image into a PDF (PDFs pass through). */
  documentPdf: (id: number | string) => `/api/documents/${id}/pdf`,
  academicYears: '/api/academic-years',
  /** Dry run of closing a year: what would be carried forward, per currency. */
  yearRolloverPreview: (id: number | string) => `/api/academic-years/${id}/rollover`,
  yearClose: (id: number | string) => `/api/academic-years/${id}/close`,
  yearCloses: '/api/year-closes',
  terms: '/api/terms',
  classes: '/api/classes',
  feeCategories: '/api/fee-categories',
  feeStructures: '/api/fee-structures',
  invoices: '/api/invoices',
  invoice: (id: number | string) => `/api/invoices/${id}`,
  invoiceVoid: (id: number | string) => `/api/invoices/${id}/void`,
  invoiceBulk: '/api/invoices/bulk',
  payments: '/api/payments',
  paymentReverse: (id: number | string) => `/api/payments/${id}/reverse`,
  receipts: '/api/receipts',
  receipt: (id: number | string) => `/api/receipts/${id}`,
  receiptReprint: (id: number | string) => `/api/receipts/${id}/reprint`,
  adjustments: '/api/adjustments',
  accounts: (studentId: number | string) => `/api/accounts/${studentId}`,
  accountLedger: (studentId: number | string) => `/api/accounts/${studentId}/ledger`,
  accountStatement: (studentId: number | string) => `/api/accounts/${studentId}/statement`,
  users: '/api/users',
  user: (id: number | string) => `/api/users/${id}`,
  userSuspend: (id: number | string) => `/api/users/${id}/suspend`,
  userActivate: (id: number | string) => `/api/users/${id}/activate`,
  userSessions: (id: number | string) => `/api/users/${id}/sessions`,
  userTempPassword: (id: number | string) => `/api/users/${id}/temporary-password`,
  approvals: '/api/approvals',
  approvalSummary: '/api/approvals/summary',
  approvalDecide: (id: number | string) => `/api/approvals/${id}/decision`,
  audit: '/api/audit',
  auditActions: '/api/audit/actions',
  accountsReconcile: '/api/accounts/reconcile',
  settings: '/api/settings',
  settingsPublic: '/api/settings/public',
  feeStructureDeactivate: (id: number | string) => `/api/fee-structures/${id}/deactivate`,
  backups: '/api/backups',
  backupRun: '/api/backups/run',
  backupStatus: '/api/backups/status/summary',
  backupVerify: (id: number | string) => `/api/backups/${id}/verify`,
  backupArchive: (id: number | string) => `/api/backups/${id}/archive`,
  reports: '/api/reports',
  report: (name: string) => `/api/reports/${name}`,
  reportExport: (name: string) => `/api/reports/${name}/export`,
} as const;

export const REPORT_NAMES = [
  'collections',
  'outstanding',
  'balances',
  'invoices',
  'receipts',
  'daily',
  'monthly',
  'by-class',
  'by-category',
  'overdue',
  'reversals',
  'adjustments',
  'reconciliation',
  'audit',
] as const;

export type ReportName = (typeof REPORT_NAMES)[number];

export const REPORT_LABELS: Record<ReportName, string> = {
  collections: 'Payment collection report',
  outstanding: 'Outstanding fees report',
  balances: 'Student balances report',
  invoices: 'Invoice report',
  receipts: 'Receipt report',
  daily: 'Daily collections',
  monthly: 'Monthly collections',
  'by-class': 'Fees by class',
  'by-category': 'Fees by category',
  overdue: 'Overdue balances',
  reversals: 'Refunds and reversals',
  adjustments: 'Adjustments and waivers',
  reconciliation: 'Transaction reconciliation',
  audit: 'Financial activity audit',
};
